import { motion, AnimatePresence } from 'framer-motion'
import { X, Users, Calendar, ClipboardList, FileText, BarChart3, MessageSquare, Bell, User, Home, BookOpen, Brain, LogOut } from 'lucide-react'

interface MentorMobileMenuProps {
    isOpen: boolean
    onClose: () => void
    activeTab: string
    onNavigate: (tab: string) => void
}

const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'students', label: 'Assigned Students', icon: Users },
    { id: 'plan-builder', label: 'Daily Plan Builder', icon: Calendar },
    { id: 'attendance', label: 'Approval Queue', icon: ClipboardList },
    { id: 'materials', label: 'Study Materials', icon: BookOpen },
    { id: 'tests', label: 'Test Management', icon: FileText },
    { id: 'question-bank', label: 'Question Bank', icon: Brain },
    { id: 'analytics', label: 'Student Analytics', icon: BarChart3 },
    { id: 'chat', label: 'Chat', icon: MessageSquare, badge: 3 },
    { id: 'announcements', label: 'Announcements', icon: Bell },
    { id: 'profile', label: 'Profile', icon: User },
]

export function MentorMobileMenu({ isOpen, onClose, activeTab, onNavigate }: MentorMobileMenuProps) {
    const handleItemClick = (itemId: string) => {
        onNavigate(itemId)
        onClose()
    }

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 lg:hidden"
                    />

                    {/* Menu Drawer */}
                    <motion.div
                        initial={{ x: '-100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '-100%' }}
                        transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                        className="fixed top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-slate-900 shadow-2xl z-50 lg:hidden overflow-y-auto"
                    >
                        {/* Header */}
                        <div className="p-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-blue to-blue-600 flex items-center justify-center">
                                    <span className="text-white font-bold text-lg">DS</span>
                                </div>
                                <div>
                                    <h2 className="font-bold text-theme-primary">Dr. Sarah</h2>
                                    <p className="text-xs text-theme-secondary">Mentor Portal</p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <X className="h-5 w-5 text-theme-secondary" />
                            </button>
                        </div>

                        {/* Menu Items */}
                        <div className="p-3 space-y-1">
                            {menuItems.map((item) => {
                                const Icon = item.icon
                                const isActive = activeTab === item.id

                                return (
                                    <button
                                        key={item.id}
                                        onClick={() => handleItemClick(item.id)}
                                        className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${isActive
                                            ? 'bg-gradient-to-r from-primary-blue to-blue-600 text-white shadow-lg'
                                            : 'text-theme-secondary hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-theme-primary'
                                            }`}
                                    >
                                        <Icon className={`h-5 w-5 ${isActive ? 'text-white' : ''}`} />
                                        <span className={`font-medium text-sm ${isActive ? 'text-white' : ''}`}>
                                            {item.label}
                                        </span>
                                        {item.badge && (
                                            <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-bold ${isActive ? 'bg-white/20 text-white' : 'bg-red-500 text-white'
                                                }`}>
                                                {item.badge}
                                            </span>
                                        )}
                                    </button>
                                )
                            })}
                        </div>

                        {/* Footer */}
                        <div className="p-3 border-t border-slate-200 dark:border-slate-800 mt-auto">
                            <button
                                onClick={() => {
                                    // Handle logout logic here or accept a prop
                                    // For now using window.location for simplicity if auth hook not available in context of this snippet
                                    // But better to use the hook if available.
                                    // Assuming explicit logout implementation request
                                    window.location.href = '/'
                                }}
                                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                            >
                                <LogOut className="h-5 w-5" />
                                <span className="font-medium text-sm">Logout</span>
                            </button>
                            <p className="text-xs text-theme-secondary text-center mt-4">
                                © 2024 Future Founder. All rights reserved.
                            </p>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    )
}
